import { useMemo } from 'react'
import type { Key, ReactNode } from 'react'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import { SelectableCard } from './SelectableCard'
import { cardListContainerStyles } from './CardListStyles'
import { LOCKED_OPTIONS_COLLAPSE_DURATION, useLockedOptionsPreview } from './useLockedOptionsPreview'

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const LockedOptionsContainer = styled.div`
  ${cardListContainerStyles}
`

const OptionWrapper = styled.div`
  display: grid;
  grid-template-rows: 1fr;
  transition:
    grid-template-rows ${LOCKED_OPTIONS_COLLAPSE_DURATION}ms ease,
    opacity ${LOCKED_OPTIONS_COLLAPSE_DURATION}ms ease,
    transform ${LOCKED_OPTIONS_COLLAPSE_DURATION}ms ease,
    margin-top ${LOCKED_OPTIONS_COLLAPSE_DURATION}ms ease;
  opacity: 1;
  transform: translateY(0) scale(1);

  &.with-gap {
    margin-top: ${rem(12)};
  }

  &.is-hidden {
    grid-template-rows: 0fr;
    opacity: 0;
    transform: translateY(${rem(-12)}) scale(0.96);
    margin-top: 0;
    pointer-events: none;
  }
`

const OptionContent = styled.div`
  min-height: 0;
  overflow: hidden;
`

const LockedOptionsFooter = styled.div`
  margin-top: ${rem(12)};
`

const LockedOptionsSummaryTile = styled(SelectableCard)`
  display: block;
  width: 100%;
  padding: ${rem(16)};
  text-align: center;
`

interface RenderOptionArgs<TOption> {
  option: TOption
  index: number
  isSelected: boolean
}

interface Props<TOption> {
  options: Array<TOption>
  disabled?: boolean
  selectedIndices: Set<number>
  getOptionKey: (option: TOption, index: number) => Key
  renderOption: (args: RenderOptionArgs<TOption>) => ReactNode
}

function LockedOptionsList<TOption>({
  options,
  disabled = false,
  selectedIndices,
  getOptionKey,
  renderOption,
}: Props<TOption>) {
  const intl = useIntl()
  const {
    isExpanded,
    shouldRenderAllOptions,
    shouldShowSummaryTile,
    shouldShowToggleButton,
    collapsedSelectedIndices,
    handleExpand,
    handleCollapse,
    shouldHideOption,
  } = useLockedOptionsPreview({
    disabled,
    selectedIndices,
    optionsLength: options.length,
  })

  const collapsedOptions = useMemo(() => {
    return collapsedSelectedIndices.map(index => ({ option: options[index], index })).filter(
      (entry): entry is { option: TOption; index: number } => entry.option !== undefined,
    )
  }, [collapsedSelectedIndices, options])

  const renderedOptions = useMemo(() => {
    if (shouldRenderAllOptions) {
      return options.map((option, index) => ({ option, index }))
    }

    return collapsedOptions
  }, [collapsedOptions, options, shouldRenderAllOptions])

  const seeAllCountLabel = intl.formatMessage(
    {
      id: 'msexlJl8TlmcuC9ePcB5',
      defaultMessage: 'See all {count} options',
      description: 'Locked-history summary tile label for card options when nothing was selected in trip planner chat',
    },
    { count: options.length },
  )
  const seeAllOptionsLabel = intl.formatMessage({
    id: 'bo2aWe1ir0Tp-JCtiujH',
    defaultMessage: 'See all options',
    description: 'Locked-history tertiary button label that expands all card options in trip planner chat',
  })
  const hideOptionsLabel = intl.formatMessage({
    id: 'NYEOony-0VN2GHa2FMi-',
    defaultMessage: 'Hide options',
    description: 'Locked-history tertiary button label that collapses card options in trip planner chat',
  })
  const toggleButtonLabel = isExpanded ? hideOptionsLabel : seeAllOptionsLabel

  if (options.length === 0) {
    return null
  }

  return (
    <LockedOptionsContainer>
      {shouldShowSummaryTile && (
        <LockedOptionsSummaryTile
          onClick={handleExpand}
          role="button"
          aria-expanded={false}
        >
          <BodyText variant="medium" colour="neutral-one" weight="bold">
            {seeAllCountLabel}
          </BodyText>
        </LockedOptionsSummaryTile>
      )}
      {!shouldShowSummaryTile && (
        <OptionsContainer>
          {renderedOptions.map(({ option, index }, renderedIndex) => {
            const isHidden = shouldHideOption(index)
            const wrapperClassName = clsx({
              'with-gap': renderedIndex > 0 && !isHidden,
              'is-hidden': isHidden,
            })

            return (
              <OptionWrapper
                key={getOptionKey(option, index)}
                className={wrapperClassName}
                aria-hidden={isHidden || undefined}
              >
                <OptionContent>
                  {renderOption({
                    option,
                    index,
                    isSelected: selectedIndices.has(index),
                  })}
                </OptionContent>
              </OptionWrapper>
            )
          })}
        </OptionsContainer>
      )}
      {shouldShowToggleButton && (
        <LockedOptionsFooter>
          <TextButton
            kind="tertiary"
            size="small"
            onClick={isExpanded ? handleCollapse : handleExpand}
            aria-expanded={isExpanded}
          >
            {toggleButtonLabel}
          </TextButton>
        </LockedOptionsFooter>
      )}
    </LockedOptionsContainer>
  )
}

export default LockedOptionsList
